import React, { useState, useCallback } from 'react';
import {
  Button,
  Divider,
  Row,
  Col,
  Input,
  Form,
  message,
  InputNumber,
  Checkbox,
  Alert,
  Select,
} from 'antd';
import api from '@services/api';
import { useTranslation } from 'react-i18next';
import SelectCustomers from '@components/SelectCustomers';
import { toBytes } from '@utils/formatters';
import * as S from './styles';

const { Item } = Form;

export interface SharedPlanPayload {
  id?: number;
  description: string;
  automaticBlocking: boolean;
  departmentId: number;
  franchiseBytes: number;
}

interface NewSharedPlanModalProps {
  open: boolean;
  onClose: () => void;
  mutateData: (data: SharedPlanPayload) => void;
}

const NewSharedPlanModal: React.FC<NewSharedPlanModalProps> = ({
  open,
  onClose,
  mutateData,
}) => {
  const { t } = useTranslation(['groupings', 'attributes']);
  const [form] = Form.useForm<SharedPlanPayload>();

  const [saveLoading, setSaveLoading] = useState(false);
  const [franchiseUnit, setFranchiseUnit] = useState<'KB' | 'MB' | 'GB'>('MB');

  const createBundledSharedPlan = useCallback(
    (payload: SharedPlanPayload) =>
      api.post(`/service-proxy/broker/sharing-plans`, payload),
    [],
  );

  const handleOnClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const handleFormFinish = useCallback(
    async (payload: SharedPlanPayload) => {
      setSaveLoading(true);
      try {
        const payloadWithBytes = {
          ...payload,
          franchiseBytes: toBytes(
            Number(payload.franchiseBytes),
            franchiseUnit,
          ),
        };
        await createBundledSharedPlan(payloadWithBytes);
        mutateData(payloadWithBytes);
        message.success(t('msgs.sharedPlanCreatedSuccessfully'));
        handleOnClose();
      } catch {
        message.error(t('msgs.errorCreatingSharedPlan'));
      }
      setSaveLoading(false);
    },
    [createBundledSharedPlan, franchiseUnit, handleOnClose, mutateData, t],
  );

  return (
    <S.ModalContainer
      bodyStyle={{ padding: '24px 40px' }}
      width={500}
      title={t('newSharedPlan')}
      open={open}
      onCancel={handleOnClose}
      destroyOnClose
      footer={null}
    >
      <Alert
        showIcon
        type="warning"
        style={{ marginBottom: 32 }}
        description={t('msgs.sharedPlanDescription')}
      />
      <Form
        form={form}
        scrollToFirstError
        onFinish={handleFormFinish}
        layout="vertical"
      >
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Item
              label={t('attributes:description')}
              name="description"
              rules={[
                {
                  required: true,
                  message: t('msgs.fillThisField'),
                },
              ]}
            >
              <Input />
            </Item>
          </Col>
          <Col span={24}>
            <Item
              label={t('attributes:department')}
              name="departmentId"
              rules={[
                {
                  required: true,
                  message: t('msgs.selectAnOption'),
                },
              ]}
            >
              <SelectCustomers isMotBusiness />
            </Item>
          </Col>
          <Col sm={12} xs={24}>
            <Item
              label={t('attributes:franchise')}
              name="franchiseBytes"
              rules={[
                {
                  required: true,
                  message: t('msgs.fillThisField'),
                },
              ]}
            >
              <InputNumber
                addonAfter={
                  <Select
                    value={franchiseUnit}
                    onChange={setFranchiseUnit}
                    options={['KB', 'MB', 'GB'].map(item => ({
                      label: item,
                      value: item,
                    }))}
                    style={{ width: 64 }}
                  />
                }
                min={0}
                defaultValue={0}
              />
            </Item>
          </Col>
          <Col
            sm={12}
            xs={24}
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Item
              name="automaticBlocking"
              initialValue={false}
              valuePropName="checked"
              style={{ marginTop: 32, marginLeft: 8 }}
            >
              <Checkbox>{t('attributes:automaticBlocking.title')}</Checkbox>
            </Item>
          </Col>
        </Row>

        <Divider />

        <Row justify="end" gutter={[8, 8]}>
          <Button loading={saveLoading} type="primary" htmlType="submit">
            {t('buttons.confirm')}
          </Button>
        </Row>
      </Form>
    </S.ModalContainer>
  );
};

export default NewSharedPlanModal;
